import axios from "axios";
import { getAnalytics } from "firebase/analytics";
// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { NotificationManager } from 'react-notifications';
import linkifyHtml from "linkify-html";

import { isDashboardChromeEmbed } from '../../chromeEmbed';
import config from '../../config';
import sharedContext from "../../sharedContext";

const TwilioChat = require("@twilio/conversations");

const firebaseConfig = {
  apiKey: "AIzaSyD0jUP2gF3FggZFa8RrdfdrXuXDJhxD9fU",
  authDomain: "cp-concierge-notifications.firebaseapp.com",
  databaseURL: "https://cp-concierge-notifications.firebaseio.com",
  projectId: "cp-concierge-notifications",
  storageBucket: "cp-concierge-notifications.appspot.com",
  messagingSenderId: "896718494619",
  appId: "1:896718494619:web:f7963e939c2ab574751f3e",
  measurementId: "G-7NXD9KM7L5"
  };
  
const firebase = initializeApp(firebaseConfig)
const analytics = getAnalytics(firebase)
const messaging = window.location.protocol === "https:" ? getMessaging(firebase) : null

// Read at call time: the dashboard rotates its API token, and a header captured at
// module load goes stale in exactly the tabs most likely to be forgotten in the
// background — which then 401 on every token fetch forever.
function reqConfig() {
  return {
    headers: {
      'Authorization': `Token ${JSON.parse(window.localStorage.getItem('bootstrap'))?.token}`
    }
  };
}

async function getTwilioToken() {

  console.log("chat_utils: Fetching access token...")
  let response;
  try {
    response = await axios.get(`${config.v1ApiUrl}/concierge/twilio/access-token/`, reqConfig());
  } catch (e) {
    if (e?.response?.status === 401 || e?.response?.status === 403) {
      // The DASHBOARD session is dead, not Twilio. Retrying can never succeed until
      // the user signs in again, and a tab that keeps trying anyway churns doomed
      // half-connections that can kick a healthy tab off a shared chat identity.
      const err = new Error(`dashboard session expired (HTTP ${e.response.status}) — reload the page and sign in again`);
      err.authExpired = true;
      throw err;
    }
    throw e;
  }
  console.log("chat_utils: Access token fetched!")

  const { data } = response;
  return data.fcm_token_web;
};

// ---- Multi-tab chat presence -------------------------------------------------
// Two dashboard tabs share one Twilio identity (shared logins have no per-tab
// endpoint suffix) and this origin's persisted twilsock state, so their clients can
// boot each other off mid-handshake ("initFailed ... disconnected" with a healthy
// network). We can't stop the SDK fight from here, but we can name it: every tab
// answers pings while it holds (or is building) a client, and a tab whose init just
// failed asks before blaming the network — the user can fix a duplicate window
// themselves, while "disconnected" alone reads as an outage.
const chatPresenceTabId = Math.random().toString(36).slice(2);
const chatPresenceChannel = typeof BroadcastChannel !== "undefined"
  ? new BroadcastChannel("cp-chat-client-presence")
  : null;
if (chatPresenceChannel) {
  chatPresenceChannel.onmessage = (ev) => {
    const msg = ev?.data;
    if (msg?.type !== "ping" || msg.from === chatPresenceTabId) return;
    if (sharedContext.get('twilioClient') !== null || sharedContext.get('twilioClientIsInitializing') === true) {
      chatPresenceChannel.postMessage({ type: "pong", from: chatPresenceTabId, to: msg.from });
    }
  };
}

/** True if another same-origin tab currently holds (or is building) a chat client. */
function otherTabHasChatClient(timeoutMs = 400) {
  if (!chatPresenceChannel) return Promise.resolve(false);
  return new Promise((resolve) => {
    const done = (result) => {
      clearTimeout(timer);
      chatPresenceChannel.removeEventListener("message", onMessage);
      resolve(result);
    };
    const onMessage = (ev) => {
      const msg = ev?.data;
      if (msg?.type === "pong" && msg.to === chatPresenceTabId) done(true);
    };
    const timer = setTimeout(() => done(false), timeoutMs);
    chatPresenceChannel.addEventListener("message", onMessage);
    chatPresenceChannel.postMessage({ type: "ping", from: chatPresenceTabId });
  });
}

const CLIENT_INIT_TIMEOUT_MS = 30000;

/**
 * Resolve once the SDK has really initialized, reject if it can't.
 *
 * The Conversations client hides initialization failures: `_ensureReady` is built
 * with a `.catch` that resolves to `undefined` unless `throwErrorsAlways` is set,
 * while `_conversationsEntity` is only assigned by a successful `_initialize()`
 * (which needs twilsock to connect first). So `await client._ensureReady` inside
 * `getConversationByUniqueName` sails through on a client that never connected and
 * the next line dies with "Cannot read properties of undefined (reading
 * 'myConversationsRead')" — a TypeError with no hint of the real cause.
 *
 * `once` on the SDK's emitter does not replay, so this must be called before the
 * first await after constructing the client.
 */
function waitForClientReady(client) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = (fn, arg) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      // `once` only auto-removes the listener that fired; drop the other one too.
      client.off("initialized", onInitialized);
      client.off("initFailed", onInitFailed);
      fn(arg);
    };
    const onInitialized = () => settle(resolve);
    const onInitFailed = ({ error }) =>
      settle(reject, new Error(`Twilio client initFailed: ${error?.message || "unknown error"}`));
    const timer = setTimeout(
      () => settle(reject, new Error("Twilio client did not finish initializing in time")),
      CLIENT_INIT_TIMEOUT_MS
    );
    client.once("initialized", onInitialized);
    client.once("initFailed", onInitFailed);
  });
}

/** Can this client open conversations, or did it never finish initializing? */
function clientCanOpenConversations(client) {
  // Deliberately reaching for the internal that getConversationByUniqueName /
  // getConversationBySid dereference: when it's missing they throw a TypeError
  // instead of anything catchable by feature.
  return !!client && !!client._conversationsEntity;
}

async function initChat() { 
 try {
  // in case this gets called while the client is being initialized,
  // block it until it is initialized and then return
  if(sharedContext.get('twilioClientIsInitializing') === true || sharedContext.get('twilioClient') !== null){
    return;
  }

  sharedContext.set('twilioClientIsInitializing', true, false);
  //console.debug("chat_utils: initChat: Initializing Twilio chat client...")
   // loading a token from server
  const token = await getTwilioToken();

  const client = new TwilioChat.Client(token);
  // Subscribe now, before any await: these events don't replay.
  const clientIsReady = waitForClientReady(client);
  // The real await is after the FCM setup below; attach a handler now so an early
  // initFailed doesn't surface as a spurious unhandledrejection in the meantime.
  clientIsReady.catch(() => {});

  //console.debug("chat_utils: initChat: client:", client)
 
  if (!isDashboardChromeEmbed()) {
  try{
    const serviceWorkerRegistration = await navigator
      .serviceWorker
      .register('/firebase-messaging-sw.js').then(function(reg){
          if(reg.installing) {
            console.log('chat_utils: Service worker installing');
          } else if(reg.waiting) {
            console.log('chat_utils: Service worker installed');
          } else if(reg.active) {
            console.log('chat_utils: Service worker active');
          }
          navigator.serviceWorker.ready.then(function(reg) {

      getToken(messaging, { vapidKey: 'BKku1TR6cRdjw4iObLcNL8d6DRMWbClRKOJgBXk5fTgdbCg8YgJsCW4_D8OrtE08enuvT-PghaSyYQH7e7uBWZc', reg }).then((currentToken) => {
        if (currentToken) {
          //console.debug("TOKEN", currentToken)
          console.log('chat_utils: getToken: Success. Registering with client...');
          Notification.requestPermission().then(function (permission) {
            console.log("chat_utils: Notifications permission:", permission)
          });

          client.setPushRegistrationId('fcm', currentToken);
          // Send the token to your server and update the UI if necessary
          // ...
          onMessage(messaging, async (payload)  => {
            //console.debug('chat_utils: Push Notification onMessage: ', payload);

            if (payload.data.twi_message_type === "twilio.channel.consumption_update"){
              return
            }

            const conversation_title = payload.data.conversation_title;
            const chat_channel_id = conversation_title.split("::")[1];
            
            let message_body = payload.data.twi_body.split("::")[0]
            let author = payload.data.author;

            const old_assistant_text = "Assistant: ";
            
            if (message_body.startsWith(old_assistant_text)){
              message_body = message_body.replace(old_assistant_text, '');
            }
            // strip out html tags
            message_body = message_body.replace(/<\/?[^>]+(>|$)/g, "").replace(/&amp;/g, '&')

            /*
            // can be used to get the authors display_name from our backend
            const response = await axios.get(`${config.apiUrl}/chat/user_by_identity/${author}/`, reqConfig()).then(response => {
              console.log("RESPONSE:", response)
              author = response.data.display_name;
            }).catch((error) => {
                if( error.response ){
                  console.warn("Unable to fetch user by identity. Error: ", error.response);
                }
            });
            */

            NotificationManager.info(message_body, "New Message", 5000, () => {
              // if clicked, take them to the chat
              window.reactHistory.push(`/chat/${chat_channel_id}/conversations/${conversation_title}/interactivechat`);
            });
            
            // Play notification sound if enabled (foreground messages)
            playNotificationSoundIfEnabled();
            
          });



        } else {
          // Show permission request UI
          console.error('chat_utils: initChat: No registration token available. Request permission to generate one.');
          // ...
        }
      }).catch((err) => {
        if (err?.code === 'messaging/permission-blocked' || err?.code === 'messaging/permission-default') {
          console.warn('chat_utils: initChat: Notification permission not granted. Push notifications disabled.');
        } else {
          console.error('chat_utils: initChat: An error occurred while retrieving token. ', err);
        }
      })
    })

    //console.error('chat_utils: posting URL to service worker ', sharedContext.get('topLogoURL'));
    //const sw = reg.installing || reg.waiting || reg.active
    //sw.postMessage(JSON.stringify({logo_url: sharedContext.get('topLogoURL')}))

  })} catch (e) {
    console.error("chat_utils: initChat: Could not set up service worker. Push notifications will not work! Error:", e)
  }
  } else {
    console.log('chat_utils: initChat: Skipping FCM / Web Push setup in Chrome extension embed (cp_embed=1).');
  }

  //console.debug("MESSAGING", messaging)

  client.on("connectionError", (error) => {
    console.warn('chat_utils: initChat: Twilio connection error:', error?.message || error);
  });

  // Token refresh, bounded. An unhandled throw here (e.g. the dashboard session
  // expired hours ago in a background tab) used to leave the SDK re-emitting
  // tokenExpired and this tab hammering the token endpoint forever — and each doomed
  // reconnect can kick a working tab's connection off a shared identity. On an auth
  // failure, or three consecutive failures of any kind, retire this client instead;
  // the next explicit open rebuilds from scratch or surfaces the stored reason.
  let tokenRefreshFailures = 0;
  const refreshTwilioToken = async (eventName) => {
    try {
      const freshToken = await getTwilioToken();
      client.updateToken(freshToken);
      tokenRefreshFailures = 0;
    } catch (e) {
      tokenRefreshFailures += 1;
      console.warn(`chat_utils: token refresh (${eventName}) failed (#${tokenRefreshFailures}):`, e?.message || e);
      if (e?.authExpired || tokenRefreshFailures >= 3) {
        sharedContext.set('twilioClientLastInitError', `${e?.message || e} (at ${new Date().toISOString()})`, false);
        if (sharedContext.get('twilioClient') === client) {
          sharedContext.set('twilioClient', null, false);
        }
        try { client.shutdown().catch(() => {}); } catch (ignored) { /* best effort */ }
      }
    }
  };
  client.on("tokenAboutToExpire", () => refreshTwilioToken("tokenAboutToExpire"));
  client.on("tokenExpired", () => refreshTwilioToken("tokenExpired"));

   //const channels = await client.getSubscribedChannels()

   //await loadMessages(channels)
   //setEventListeners(client, channels)

   // Only publish a client that can actually serve conversations. Caching a
   // half-initialized one is worse than caching nothing: every later open reuses
   // it and fails the same way until the tab is reloaded.
   try {
     await clientIsReady;
   } catch (initErr) {
     // Don't leave the doomed client's websocket retrying in the background next to
     // the fresh client the next getTwilioClient() will build.
     try { client.shutdown().catch(() => {}); } catch (ignored) { /* best effort */ }
     throw initErr;
   }

   sharedContext.set('twilioClient', client, false);

   sharedContext.set('twilioClientIsInitializing', false, false);
   sharedContext.set('twilioClientLastInitError', null, false);
   console.log("chat_utils: initChat: Chat client initialized.")
   navigator.setAppBadge(0)
 } catch (e) {
   console.error("chat_utils: initChat: error:", e)
   //console.log("chat_utils: initChat: client", sharedContext.get('twilioClient'))
   // Keep the reason around so the "Chat client unavailable" report can say WHY
   // (token fetch failure vs initFailed vs init timeout) — the alert emails are
   // undiagnosable without it.
   let reason = `${e?.message || e}`;
   // A second dashboard tab holding the same chat identity is the classic cause of
   // "disconnected during connection initialisation". Check and say so: the user can
   // close the other window themselves, and support can't see it from a stack trace.
   try {
     if (!e?.authExpired && await otherTabHasChatClient()) {
       reason += " — chat is connected in another dashboard tab or window; close the other window (check minimized windows too) and retry";
     }
   } catch (ignored) { /* best effort */ }
   sharedContext.set('twilioClientLastInitError', `${reason} (at ${new Date().toISOString()})`, false);
   sharedContext.set('twilioClientIsInitializing', false, false);
 }
}

function getLastTwilioInitError() {
  return sharedContext.get('twilioClientLastInitError') || null;
}

async function getTwilioClient(attemptNum=0) {
  attemptNum += 1;
  const isInitializing = sharedContext.get('twilioClientIsInitializing');
  const client = sharedContext.get('twilioClient');
  //console.debug("chat_utils: getTwilioClient called");
  //console.debug("chat_utils: getTwilioClient: isInitializing: ", isInitializing);
  //console.debug("chat_utils: getTwilioClient: client: ", client);
  
  try {
    if (client !== null){
      if (clientCanOpenConversations(client)) {
        console.log("chat_utils: Client already intitialized. Using existing client.")
        //console.debug("chat_utils: getTwilioClient: Twilio client already initialized. Accessing existing client.")
        return client;
      }
      // Cached but unusable (initialization never completed, or the client was shut
      // down). Rebuild with a fresh token instead of handing it out again.
      console.warn("chat_utils: getTwilioClient: cached Twilio client can't open conversations; rebuilding.")
      sharedContext.set('twilioClient', null, false);
      sharedContext.set('twilioClientIsInitializing', false, false);
      await initChat();
      return sharedContext.get('twilioClient');
    }
    else if (client === null && isInitializing !== true){
      console.log("chat_utils: Client not yet intitialized. Initializing client...")
      //console.debug("chat_utils: getTwilioClient: Twilio client not initialized. Initializing client...")
      await initChat();
      const client = sharedContext.get('twilioClient');
      return client;
    }
    else if (isInitializing === true) {
      // in case this gets called while the client is being initialized,
      // block it until it is initialized and then return
      console.debug("chat_utils: getTwilioClient: Critical section - waiting for client... attemptNum=", attemptNum)

      // The following is somewhat of a hack to prevent hanging if the Twilio client isn't
      // loading for some reason. It also helps prevent inconsistent states from a race condition
      // (for instance, if twilioClientIsInitializing is true but the client isn't actually
      // in an initialization process; logging out and logging back in should work in that 
      // case as well)
      // Must outlast CLIENT_INIT_TIMEOUT_MS (initChat now clears the flag itself within
      // that bound): force-clearing while a real init is in flight starts a second
      // initChat and one of the two clients ends up orphaned with a live websocket.
      if (attemptNum > (CLIENT_INIT_TIMEOUT_MS / 100) + 50){
        sharedContext.set('twilioClientIsInitializing', false, false);
      }
      const client = await new Promise((resolve, reject) => {
                      setTimeout(() => resolve(getTwilioClient(attemptNum)), 100)
                      });
      return client
    }
    else{
      console.debug("chat_utils: TOCTOU???")
    }
  } catch (e) {
    console.error("chat_utils: getTwilioClient: error:", e)
  }
 }
 
async function loadChannelMessages(channelSid, channels) {
 const messages = await channels.items.find(c => c.sid === channelSid).getMessages()
}
 
async function loadMessages(channels) {
 const messagesArr = await Promise.all(
   channels.items.map(async channel => ({
     sid: channel.sid,
     messages: await channel.getMessages()
   }))
 )
 
 // creating a messages map object
 const messages = messagesArr.reduce((acc, current) => {
   acc[current.sid] = current.messages
   return acc
 }, {})
}
 
function setEventListeners(chat) {
 chat.on('channelJoined', async channel => {
 })
}
 
function sendMessage(message, channel) {
 channel.sendMessage(message)
}

/*
  if (navigator.serviceWorker.controller) {
      // Yes, send our controller a message.
      console.log("Sending 'hi' to controller");
      navigator.serviceWorker.controller.postMessage({ api_token: JSON.parse(window.localStorage.getItem('bootstrap')).token } );
  } else {
      // No, register a service worker to control pages like us.
      // Note that it won't control this instance of this page, it only takes effect
      // for pages in its scope loaded *after* it's installed.
      navigator.serviceWorker.register("firebase-messaging-sw.js")
          .then(function(registration) {
              console.log("Service worker registered, scope: " + registration.scope);
              console.log("Refresh the page to talk to it.");
              window.location.reload();
              // If we want to, we might do `location.reload();` so that we'd be controlled by it
          })
          .catch(function(error) {
              console.log("Service worker registration failed: " + error.message);
          });
  }
*/

// Escape raw message text before linkify-html / dangerouslySetInnerHTML. linkify-html parses its
// input as HTML, so a casual "< this booking..." becomes a bogus tag and DOMPurify then strips
// everything after the "<" — which is exactly how chat bubbles were getting cut off.
function escape_html(text) {
  if (text == null || text === '') {
    return text;
  }
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// https://stackoverflow.com/questions/1500260/detect-urls-in-text-with-javascript (Niaz Mohammed)
// `text` may already contain intentional HTML (from simple_markdown_to_html / whatsapp_markup /
// civitatis_extranet_link). Callers passing plain text must escape_html() first.
function linkify(text) {
  if(!text){
    return '';
  }

  const options = { defaultProtocol: "https", target: "_blank", rel: "noopener noreferrer", attributes: {style: "word-break: break-word"} };
  let s = linkifyHtml(
    text,
    options
  );

  if (s && s.startsWith("touroptima.com")){
    s = "https://" + s;
  }

  return s;
  /*
  var urlRegex =/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,}(\/[-a-zA-Z0-9@:%_\+~#&//=]+)*(\/?[?][^\s])?(([-a-zA-Z0-9@:%_\+~#&;//=,]|(\.\d)|(-\d)|(\.\w)|(\.\+)|(!\d)|(\?[a-zA-z_]+=))*)(\.[a-zA-Z0-9]{2,6})?\b/ig;
  return text.replace(urlRegex, function(url) {
    if(url.toLowerCase().startsWith('http') ){
      return '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + url + '</a>';
    }
    else{
      return '<a href="//' + url + '" target="_blank" rel="noopener noreferrer">' + url + '</a>';
    }
  });*/
}

// CP-8139: the escape_html step below (added so linkify-html can't mis-parse a stray "<" and
// have DOMPurify truncate the bubble) also escapes intentional HTML already present in message
// bodies — automated messages emit literal <strong>...</strong>, which then rendered as the
// visible text "<strong>" instead of bold. Restore a small allow-list of formatting tags after
// escaping. Only bare, attribute-free tags match, so "< this booking" or a tag carrying
// attributes (e.g. onclick=) stays escaped — the truncation fix holds — and DOMPurify still
// sanitizes the final HTML downstream.
const ALLOWED_ESCAPED_TAG_RE = /&lt;(\/?)(strong|em|b|i|u|s|br)\s*\/?&gt;/gi;
function restore_allowed_html_tags(text) {
  return text.replace(ALLOWED_ESCAPED_TAG_RE, '<$1$2>');
}

// Operators paste HTML email templates into chat (e.g. Paxflow pickup notices with
// <a href="...">customer portal</a>), which the CP-8139 escape shows as raw tag text — a guest
// on BusTravel op 73 got a pickup notice whose portal link displayed as a literal <a href=...>
// tag. Decision (green-lit by Ben in a Google Chat discussion, 8/17/2026): render <a> tags for
// TRUSTED message sources only — admin-authored or automated system messages; the caller
// decides via simple_markdown_to_html's allow_links option — by un-escaping those anchors back
// into real links. Guest messages must never get this: a guest-pasted anchor stays visible as text,
// so a phishing href can't hide behind innocuous link text.
// Conservative on purpose: only http(s) hrefs qualify (javascript: etc. stay escaped), and every
// attribute except the href is discarded before DOMPurify sanitizes downstream. An anchor with
// empty inner text (seen in the wild: BusTravel's edited Paxflow template) gets its href as the
// visible label — a faithful render would paint zero pixels.
const ESCAPED_ANCHOR_RE = /&lt;a\s+((?:(?!&gt;)[\s\S])*?)&gt;((?:(?!&lt;\/a)[\s\S])*?)&lt;\/a\s*&gt;/gi;
function restore_allowed_link_tags(text) {
  return text.replace(ESCAPED_ANCHOR_RE, function (wm, attrs, inner) {
    const href_match = attrs.match(/href\s*=\s*(["'])(https?:\/\/(?:(?!&gt;)[\s\S])*?)\1/i);
    if (!href_match) {
      return wm; // no usable http(s) href — leave the whole tag escaped/visible
    }
    const href = href_match[2].replace(/"/g, '%22');
    const label = inner.trim() !== '' ? inner : href;
    return '<a href="' + href + '" target="_blank" rel="noopener noreferrer" style="word-break: break-word">' + label + '</a>';
  });
}

function simple_markdown_to_html(body, { allow_links = false } = {}){
  if(!body){
    return body;
  }

  // Escape first so raw "<" / ">" in agent text can't be parsed as HTML tags by linkify-html.
  // Markdown replacements below then insert intentional <strong>/<em>/<i> tags safely.
  let text = restore_allowed_html_tags(escape_html(body));

  // borrowed from showdown lib (https://github.com/showdownjs/showdown/blob/master/dist/showdown.js)
  text = text?.replace(/\b___(\S[\s\S]*?)___\b/g, function (wm, txt) {
    return ('<strong><em>' + txt + '</em></strong>');
  });
  text = text?.replace(/\b__(\S[\s\S]*?)__\b/g, function (wm, txt) {
    return ('<strong>' + txt + '</strong>');
  });
  text = text?.replace(/\b_(\S[\s\S]*?)_\b/g, function (wm, txt) {
    return (txt, '<i>' + txt + '</i>');
  });
  text = text?.replace(/\*\*\*(\S[\s\S]*?)\*\*\*/g, function (wm, m) {
    return (/\S$/.test(m)) ? ('<strong><em>' + m + '</em></strong>') : wm;
  });
  text = text?.replace(/\*\*(\S[\s\S]*?)\*\*/g, function (wm, m) {
    return (/\S$/.test(m)) ? ('<strong>' + m + '</strong>') : wm;
  });
  text = text?.replace(/\*([^\s*][\s\S]*?)\*/g, function (wm, m) {
    // !/^\*[^*]/.test(m) - test if it doesn't start with ** (since it seems redundant, we removed it)
    return (/\S$/.test(m)) ? ('<em>' + m + '</em>') : wm;
  });
  // Runs LAST deliberately: if a markdown pass above mangled the still-escaped anchor (e.g. a
  // "_" run inside the href), the restore regex simply fails to match and the tag stays visible
  // as escaped text — the safe failure mode. Restoring first would let markdown corrupt a real
  // href attribute instead.
  if (allow_links) {
    text = restore_allowed_link_tags(text);
  }
  return text
}

// WhatsApp text formatting (*bold*, _italic_, ~strikethrough~, --underline--, ```mono```) reuses
// characters that legitimately appear inside URLs. A booking link ending in a matched tilde pair
// (e.g. https://maps.apple/p/V.wMAZamd~qQr~) would otherwise be read as `~qQr~` strikethrough and
// have both tildes eaten, mangling the link in the History Log. WhatsApp itself never applies these
// formats inside a URL token, so we mirror that: split out http(s) URL runs and only format the
// text between them. Must run BEFORE linkify (which then wraps the untouched URLs in anchors).
function whatsapp_markup_to_html(text) {
  if (!text) {
    return text;
  }
  // Callers that insert HTML first (e.g. UserProfile's \n -> <br>) must escape_html() the raw
  // text before that step; this helper assumes entities are already safe and only adds markup tags.
  const apply_markup = (segment) => segment
    .replace(/(?:\*)(?:(?!\s))((?:(?!\*|\n).)+)(?:\*)/g, '<b>$1</b>')
    .replace(/(?: _)(?:(?!\s))((?:(?!\n|_).)+)(?:_ )/g, ' <i>$1</i> ')
    .replace(/(?:~)(?:(?!\s))((?:(?!\n|~).)+)(?:~)/g, '<s>$1</s>')
    .replace(/(?:--)(?:(?!\s))((?:(?!\n|--).)+)(?:--)/g, '<u>$1</u>')
    .replace(/(?:```)(?:(?!\s))((?:(?!\n|```).)+)(?:```)/g, '<tt>$1</tt>');

  // Stop the URL token at whitespace or `<` so an already-inserted <br> (from the \n -> <br> pass)
  // doesn't get swallowed into the "url" and skip formatting for the rest of the line.
  const url_re = /https?:\/\/[^\s<]+/g;
  let out = '';
  let last = 0;
  let m;
  while ((m = url_re.exec(text)) !== null) {
    out += apply_markup(text.slice(last, m.index));
    out += m[0]; // leave the URL untouched so its ~/*/_ etc. survive
    last = m.index + m[0].length;
  }
  out += apply_markup(text.slice(last));
  return out;
}

// The backend email pipeline appends "Civitatis Extranet:\n<huge sglink tracking URL>" to guest
// messages forwarded from Civitatis notification emails. Rendered as-is, linkify turns that into
// a screen-filling link, so swap it for a short labeled anchor instead. Must run BEFORE linkify:
// linkify-html leaves the inserted <a> tag untouched, while the reverse order would double-wrap.
function civitatis_extranet_link(text) {
  if (!text) {
    return text;
  }
  return text.replace(/Civitatis Extranet:\s*(https?:\/\/[^\s<]+)/g, function (wm, url) {
    const href = url.replace(/"/g, '%22');
    return '<strong>To reply to this Civitatis extranet message, <a href="' + href + '" target="_blank" rel="noopener noreferrer">click this link</a></strong>';
  });
}

// Same for Klook: the backend appends "REPLY HERE VIA KLOOK (REQUIRED)\n<~900-char click.klook.com
// tracking URL>" to forwarded Klook IM digests. Make the label itself the link and drop the raw URL.
// The "(Klook reply link not found …)" fallback has no URL and is left as text. Run BEFORE linkify.
function klook_reply_link(text) {
  if (!text) {
    return text;
  }
  return text.replace(/REPLY HERE VIA KLOOK \(REQUIRED\)\s*(https?:\/\/[^\s<]+)/g, function (wm, url) {
    const href = url.replace(/"/g, '%22');
    return '<strong><a href="' + href + '" target="_blank" rel="noopener noreferrer">REPLY HERE VIA KLOOK (REQUIRED)</a></strong>';
  });
}

// OTA reply-link rewrites for chat bubbles (Civitatis extranet, Klook). Run BEFORE linkify.
function ota_reply_links(text) {
  return klook_reply_link(civitatis_extranet_link(text));
}

// CP-8116: SMS/WhatsApp/Airbnb ingest appends each attachment's URL to the message text, and
// chat bodies from before the CP-8000 short-link rollout embed full S3 media URLs — so History
// Log rows show ~200-char links that drown the message. The full URL is only needed as an href,
// so swap recognized attachment/media URLs for a short labeled anchor. Must run AFTER
// escape_html (the inserted tags must survive) and BEFORE linkify (linkify-html leaves existing
// <a> tags untouched; the reverse order would double-wrap). Never run on email bodies: those are
// intentional HTML, and a URL inside src="..." would be corrupted by the replacement.

// Stored attachments/images/documents are named "(att|image|document)-<name>-<sha512-hash>.<ext>"
// (see backend attachment_upload_path / image_upload_path / document_upload_path). The FileField's
// 100-char name limit truncates the hash to whatever fits — prod rows show runs as short as 11 hex
// — so accept any trailing hex run of 8+.
const HASHED_MEDIA_BASENAME_RE = /^(att|image|document)-(.+?)-[0-9a-f]{8,}(\.[^.]+)?$/i;
const IMAGE_EXT_RE = /\.(jpe?g|jfif|png|gif|webp)$/i;
// Twilio media SIDs (ME...), UUID-ish blobs, or download_image's "downloaded-image" fallback
// filename make useless link labels; use a generic one.
const OPAQUE_MEDIA_NAME_RE = /^(ME[0-9a-f]{20,}|[0-9a-f]{20,}|[0-9a-f-]{30,})$|(^|-)downloaded-image$/i;

function media_link_label(url) {
  let basename = url.split('/').pop().split('?')[0].split('#')[0];
  try {
    basename = decodeURIComponent(basename);
  } catch (e) { /* malformed escape — keep the raw basename */ }
  const m = basename.match(HASHED_MEDIA_BASENAME_RE);
  if (m) {
    // Names can nest another generated prefix (e.g. "document-att-CP_Manual-<hash>.docx" from a
    // re-stored attachment) — strip those to recover the original filename.
    let name = m[2];
    while (/^(att|image|document)-/i.test(name)) {
      name = name.replace(/^(att|image|document)-/i, '');
    }
    const ext = m[3] || '';
    const isImage = m[1].toLowerCase() === 'image' || IMAGE_EXT_RE.test(ext);
    if (!name || OPAQUE_MEDIA_NAME_RE.test(name)) {
      return isImage ? 'Photo' : 'Attachment';
    }
    return `${name}${ext}`;
  }
  // Raw provider URLs survive in rows where the ingest download-and-store step failed.
  if (/^https?:\/\/api\.twilio\.com\/[^\s<]*\/Media\//i.test(url)) {
    return 'Attachment';
  }
  if (/^https?:\/\/([^\s</]*\.)?muscache\.com\//i.test(url)) {
    return IMAGE_EXT_RE.test(basename) ? 'Photo' : 'Attachment';
  }
  return null;
}

function shorten_attachment_links(text) {
  if (!text) {
    return text;
  }
  return String(text).replace(/https?:\/\/[^\s<]+/g, function (url) {
    // Keep sentence punctuation out of the link, mirroring linkify's behavior.
    const trailing = (url.match(/[.,;:!?]+$/) || [''])[0];
    const clean = trailing ? url.slice(0, -trailing.length) : url;
    const label = media_link_label(clean);
    if (!label) {
      return url;
    }
    const href = clean.replace(/"/g, '%22');
    // A decoded %3C/%3E in the filename would otherwise reach linkify-html as a raw tag.
    const safeLabel = label.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${safeLabel}</a>${trailing}`;
  });
}

// CP-8122: true when the text is nothing but recognized stored-media URLs (plus whitespace) —
// an ingest-built body whose content is fully duplicated by the message's attached media.
// Callers pass the RAW message body (before escaping/markdown).
function is_all_media_links(text) {
  if (!text) {
    return false;
  }
  let sawMediaLink = false;
  const rest = String(text).replace(/https?:\/\/[^\s<]+/g, function (url) {
    if (media_link_label(url)) {
      sawMediaLink = true;
      return '';
    }
    return url;
  }).trim();
  return sawMediaLink && rest === '';
}

// Friendly label for a tour tag's category. Prefers the operator-defined category
// name sent by the backend (`category_name`), falling back to a title-cased version
// of the lowercase category code ('bus_number' -> 'Bus Number') for older payloads.
function tagCategoryLabel(tag) {
  if (tag?.category_name) return tag.category_name;
  const code = tag?.category;
  if (!code) return null;
  return String(code)
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Builds the hover tooltip for a set of tags (tour tags or visible-only chat
// tags), prefixing each tag value with its category, e.g.
// "Pickup: Main Lobby; Guide: Jane Doe". Tags without a known category fall
// back to just the value.
function formatTagsTooltip(tags) {
  if (!Array.isArray(tags)) return '';
  return tags
    .map((tag) => {
      const value = tag?.data?.friendly_name || tag?.name || '';
      const label = tagCategoryLabel(tag);
      return label ? `${label}: ${value}` : value;
    })
    .join('; ');
}

// Backwards-compatible alias for callers that specifically format tour tags.
const formatTourTagsTooltip = formatTagsTooltip;

const unarchive_channel = async (channel_id, restore_synchronously = true) => {
  // The backend requires the session token (it was open to anyone until 2026-10-15)
  const response = await axios.post(`${config.apiUrl}/chat/reactivate_channel/`, {
    group_tour_chat_channel_id: channel_id,
    restore_synchronously: restore_synchronously
  }, reqConfig()).then(response => {
    console.debug("ChatList: chat/reactivate_channel response: ", response)
  }).catch((error) => {
    if (error.response) {
      console.error("ChatList: chat/reactivate_channel: Error unarchiving channel!", error.response)
    }
  });
};

  // Calls our backend to ensure that the conversation is active_synced. If
  // include_user_id is true, the call is optimized to return faster (it syncs that user
  // first and returns a response before asyncronously syncing anyone else who needs synced) 
  const ensure_conversation_is_active_synced = async (conversation_id, include_user_id = true) => {
    console.debug("chat_utils: ensure_conversation_is_active_synced called with conversation_id =", conversation_id, "include_user_id =", include_user_id)
    if (!conversation_id) {
      // Null identifier used to POST through and match every incomplete row backend-side
      // (CPBACKEND-2E3W). Return null rather than undefined so callers that store the response
      // (e.g. ChatScreen's this.reactivateResponse) hold an explicit "no result".
      console.warn("chat_utils: ensure_conversation_is_active_synced skipped — no conversation_id");
      return null;
    }
    let params;
    if (include_user_id === true) {
      const u_id = JSON.parse(window.localStorage.getItem('bootstrap'))?.user?.id
      if (u_id == null) {
        // The bootstrap blob is gone (logged out in another tab), so this tab is signed out:
        // posting String(undefined) made the backend 500 (CPBACKEND-EXYP).
        const err = new Error("dashboard session expired — reload the page and sign in again");
        err.authExpired = true;
        throw err;
      }
      params = { conversation_identifier: conversation_id, user_id: String(u_id) }
    }
    else {
      params = { conversation_identifier: conversation_id }
    }
    // The backend requires the session token (it was open to anyone until 2026-10-15)
    const response = await axios.post(`${config.apiUrl}/chat/reactivate_conversation/`, params, reqConfig())

    const { data } = response;
    return data;
  };

// Preload audio file to avoid cut-off on first play
let notificationAudio = null;
function preloadNotificationAudio() {
  if (!notificationAudio) {
    notificationAudio = new Audio('/chat-notification-pop.wav');
    notificationAudio.volume = 0.5; // 50% volume
    notificationAudio.preload = 'auto';
    // Preload the audio by loading it
    notificationAudio.load();
    // Add error handler to detect loading issues
    notificationAudio.addEventListener('error', (e) => {
      console.error('chat_utils: Error loading notification audio file:', e);
      console.error('chat_utils: Audio error details:', {
        error: notificationAudio.error,
        networkState: notificationAudio.networkState,
        readyState: notificationAudio.readyState,
        src: notificationAudio.src
      });
    });
    // Do not call play() here: when autoplay is allowed it can audible on every bundle load / navigation.
    // preload='auto' + load() buffers the asset without triggering playback.
  }
  return notificationAudio;
}

// Preload audio when module loads (skip in Chrome extension embed)
if (!isDashboardChromeEmbed()) {
  preloadNotificationAudio();
}

// Play notification sound if enabled (for foreground messages)
// Checks both foreground and background settings based on tab visibility
async function playNotificationSoundIfEnabled() {
  try {
    if (isDashboardChromeEmbed()) {
      return;
    }
    // Check if tab is in background using Page Visibility API
    const isTabInBackground = document.hidden || document.visibilityState === 'hidden';
    
    const { get: IndexedDB_get } = await import('idb-keyval');
    const soundEnabledBackground = await IndexedDB_get('notificationSoundEnabledBackground');
    const soundEnabledForeground = await IndexedDB_get('notificationSoundEnabledForeground');
    
    const shouldPlaySound = isTabInBackground 
      ? soundEnabledBackground === true 
      : soundEnabledForeground === true;
    
    if (shouldPlaySound) {
      
      // Create a new Audio instance for each notification (allows simultaneous plays)
      // The preloaded audio helps with browser caching, so this should load quickly
      const audio = new Audio('/chat-notification-pop.wav');
      audio.volume = 0.5; // 50% volume
      audio.preload = 'auto';
      
      // Add error handler
      audio.addEventListener('error', (e) => {
        console.error('chat_utils: Audio playback error:', e);
        console.error('chat_utils: Audio error details:', {
          error: audio.error,
          networkState: audio.networkState,
          readyState: audio.readyState,
          src: audio.src,
          code: audio.error?.code,
          message: audio.error?.message
        });
      });
      
      // Always wait for canplay event to ensure audio is ready
      // This handles both cached and uncached scenarios
      const playWhenReady = () => {
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise
            .catch(err => {
              console.error('chat_utils: Could not play notification sound:', err);
              console.error('chat_utils: Error name:', err.name);
              console.error('chat_utils: Error message:', err.message);
              if (err.name === 'NotAllowedError') {
                console.warn('chat_utils: Browser blocked audio autoplay. User interaction may be required.');
              } else if (err.name === 'NotSupportedError') {
                console.warn('chat_utils: Audio format not supported or file not found.');
              }
            });
        }
      };
      
      // Check if already ready, otherwise wait for canplay
      if (audio.readyState >= 2) {
        playWhenReady();
      } else {
        audio.addEventListener('canplay', playWhenReady, { once: true });
        // Also try to load it explicitly
        audio.load();
      }
    }
  } catch (error) {
    console.log('chat_utils: Error checking/playing notification sound:', error);
  }
}

navigator?.serviceWorker?.addEventListener('message', (event) => {
  if (!event.data.action) {
    return
  }
  switch (event.data.action) {
    case 'redirect-from-background_notification_click':
      //window.location.href = event.data.url
      window.reactHistory.push(event.data.path);
      break
    case 'play-notification-sound':
      if (isDashboardChromeEmbed()) {
        break;
      }
      // Play notification sound in foreground page context (from service worker)
      // Service worker already checked background setting, but check current tab state for appropriate setting
      (async () => {
        try {
          const isTabInBackground = document.hidden || document.visibilityState === 'hidden';
          const { get: IndexedDB_get } = await import('idb-keyval');
          const soundEnabled = isTabInBackground 
            ? await IndexedDB_get('notificationSoundEnabledBackground')
            : await IndexedDB_get('notificationSoundEnabledForeground');
          
          if (soundEnabled === true) {
            // Create a new Audio instance for each notification (allows simultaneous plays)
            // The preloaded audio helps with browser caching, so this should load quickly
            const audio = new Audio('/chat-notification-pop.wav');
            audio.volume = 0.5; // 50% volume
            audio.preload = 'auto';
            
            // Add error handler
            audio.addEventListener('error', (e) => {
              console.error('chat_utils: Audio playback error (from service worker):', e);
              console.error('chat_utils: Audio error details:', {
                error: audio.error,
                networkState: audio.networkState,
                readyState: audio.readyState,
                src: audio.src,
                code: audio.error?.code,
                message: audio.error?.message
              });
            });
            
            // Always wait for canplay event to ensure audio is ready
            // This handles both cached and uncached scenarios
            const playWhenReady = () => {
              const playPromise = audio.play();
              if (playPromise !== undefined) {
                playPromise
                  .catch(err => {
                    console.error('chat_utils: Could not play notification sound (from service worker):', err);
                    console.error('chat_utils: Error name:', err.name);
                    console.error('chat_utils: Error message:', err.message);
                    if (err.name === 'NotAllowedError') {
                      console.warn('chat_utils: Browser blocked audio autoplay. User interaction may be required.');
                    } else if (err.name === 'NotSupportedError') {
                      console.warn('chat_utils: Audio format not supported or file not found.');
                    }
                  });
              }
            };
            
            // Check if already ready, otherwise wait for canplay
            if (audio.readyState >= 2) {
              playWhenReady();
            } else {
              audio.addEventListener('canplay', playWhenReady, { once: true });
              // Also try to load it explicitly
              audio.load();
            }
          }
        } catch (error) {
          console.log('chat_utils: Error playing notification sound:', error);
        }
      })();
      break
    // no default
  }
})
 
export {
  reqConfig,
  initChat,
  getTwilioClient,
  getLastTwilioInitError,
  loadChannelMessages,
  sendMessage,
  escape_html,
  restore_allowed_html_tags,
  restore_allowed_link_tags,
  linkify,
  simple_markdown_to_html,
  whatsapp_markup_to_html,
  civitatis_extranet_link,
  klook_reply_link,
  ota_reply_links,
  shorten_attachment_links,
  is_all_media_links,
  unarchive_channel,
  ensure_conversation_is_active_synced,
  formatTourTagsTooltip,
  formatTagsTooltip,
  tagCategoryLabel
};
